import React, { useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ReloadIcon } from "@radix-ui/react-icons";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { useHistory } from "react-router-dom";
import { Link } from "react-router-dom";
import { signIn, signOut, confirmSignIn, rememberDevice, forgetDevice } from "aws-amplify/auth";
import { useAdminAuth } from "../../contexts/AdminAuthContext";
import MFAChallenge from "../../components/MFAChallenge";
import CognitoMFASetup from "../../components/CognitoMFASetup";

const schema = yup
  .object({
    email: yup
      .string()
      .email("Invalid email address")
      .required("Email address is required."),
    password: yup
      .string()
      .min(8, "Password must be at least 8 characters")
      .required("Password is required."),
  })
  .required();

const AdminLogin = () => {
  const history = useHistory();
  const { adminUser, checkAdminAuthStatus } = useAdminAuth();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showMFAChallenge, setShowMFAChallenge] = React.useState(false);
  const [userEmail, setUserEmail] = React.useState("");
  const [isMFACompleting, setIsMFACompleting] = React.useState(false);
  const [mfaSetupRequired, setMfaSetupRequired] = React.useState(false);
  const [totpSetupDetails, setTotpSetupDetails] = React.useState(null);
  const [rememberMe, setRememberMe] = React.useState(true);

  const REMEMBER_ME_KEY = "trivistaRememberMe";

  const applyRememberMe = async () => {
    if (typeof window !== "undefined") {
      if (rememberMe) {
        const expiresAt = Date.now() + 30 * 24 * 60 * 60 * 1000;
        window.localStorage.setItem(
          REMEMBER_ME_KEY,
          JSON.stringify({ enabled: true, expiresAt })
        );
      } else {
        window.localStorage.removeItem(REMEMBER_ME_KEY);
      }
    }

    if (rememberMe) {
      try {
        await rememberDevice();
      } catch {
        // ignore device remember errors
      }
    } else {
      try {
        await forgetDevice();
      } catch {
        // ignore device forget errors
      }
    }
  };

  useEffect(() => {
    // Check if admin is already authenticated
    if (adminUser) {
      history.push("/admin/users");
    }
  }, [adminUser, history]);

  const {
    register,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);

      if (!rememberMe && typeof window !== "undefined") {
        window.localStorage.removeItem(REMEMBER_ME_KEY);
      }

      const { isSignedIn, nextStep } = await signIn({
        username: data.email.toLowerCase(),
        password: data.password,
      });

      if (isSignedIn) {
        const isAdmin = await checkAdminAuthStatus();
        
        if (isAdmin) {
          await applyRememberMe();
          history.push("/admin/users");
        } else {
          setError("root", {
            message: "Access denied. You don't have admin privileges.",
          });
          // Sign out the non-admin user
          await signOut();
        }
      } else {
        if (nextStep.signInStep === "CONFIRM_SIGN_UP") {
          setError("root", {
            message: "Please confirm your email address before signing in.",
          });
        } else if (
          nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
        ) {
          setError("root", {
            message: "Please set a new password.",
          });
        } else if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_SOFTWARE_TOKEN_MFA_CODE" || 
                   nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
          // MFA challenge required
          setShowMFAChallenge(true);
          setUserEmail(data.email);
          return;
        } else if (nextStep.signInStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
          // MFA setup required - show MFA setup component
          setMfaSetupRequired(true);
          setUserEmail(data.email);
          setTotpSetupDetails(nextStep.totpSetupDetails);
          return;
        } else {
          setError("root", {
            message: "Sign-in failed. Please check your credentials.",
          });
        }
      }
    } catch (error) {
      console.log("Admin sign-in error:", error);
      setError("root", {
        message: error.message || "An error occurred during sign-in.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMFASubmit = async (mfaCode) => {
    try {
      setIsSubmitting(true);
      setError("root", { message: "" });

      const { isSignedIn } = await confirmSignIn({
        challengeResponse: mfaCode
      });
      
      if (isSignedIn) {
        const isAdmin = await checkAdminAuthStatus();
        
        if (isAdmin) {
          await applyRememberMe();
          history.push("/admin/users");
        } else {
          // User is not admin, show error and sign out
          setError("root", {
            message: "Access denied. You don't have admin privileges.",
          });
          await signOut();
          setIsMFACompleting(false);
        }
      } else {
        setError("root", {
          message: "MFA verification failed. Please try again.",
        });
      }
    } catch (error) {
      console.error("MFA verification error:", error);
      setError("root", {
        message: error.message || "MFA verification failed. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMFACancel = () => {
    setShowMFAChallenge(false);
    setUserEmail("");
  };

  if (adminUser) return <div />;

  // Show loading screen during MFA completion
  if (isMFACompleting) {
    return (
      <div className="relative min-h-screen bg-zinc-900">
        <div
          className="fixed w-full h-full z-0 bg-cover bg-repeat bg-center"
          style={{ backgroundImage: `url(/bg-new.jpg)` }}
        />
        <div className="flex flex-col h-full items-center justify-center relative z-10">
          <div className="mt-4 md:mt-8 lg:mt-16 mb-4 md:mb-8 lg:mb-12">
            <img
              alt="logo"
              src="/TriVista_Horizontal_Logo_White.png"
              className="w-[300px]"
            />
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              <span className="text-white text-lg">Completing MFA verification...</span>
            </div>
            <p className="text-white text-sm">Redirecting to admin dashboard</p>
          </div>
        </div>
      </div>
    );
  }

  // Show MFA challenge if required
  if (showMFAChallenge) {
    return (
      <div className="relative min-h-screen bg-zinc-900">
        <div
          className="fixed w-full h-full z-0 bg-cover bg-repeat bg-center"
          style={{ backgroundImage: `url(/bg-new.jpg)` }}
        />
        <div className="flex flex-col h-full items-center justify-center relative z-10">
          <div className="mt-4 md:mt-8 lg:mt-16 mb-4 md:mb-8 lg:mb-12">
            <img
              alt="logo"
              src="/TriVista_Horizontal_Logo_White.png"
              className="w-[300px]"
            />
          </div>
          <MFAChallenge
            onSubmit={handleMFASubmit}
            onCancel={handleMFACancel}
            loading={isSubmitting}
            error={errors.root?.message}
          />
        </div>
      </div>
    );
  }

  // Show MFA setup if required
  if (mfaSetupRequired) {
    return (
      <div className="relative min-h-screen bg-zinc-900">
        <div
          className="fixed w-full h-full z-0 bg-cover bg-repeat bg-center"
          style={{ backgroundImage: `url(/bg-new.jpg)` }}
        />
        <div className="flex flex-col h-full items-center justify-center relative z-10">
          <div className="mt-4 md:mt-8 lg:mt-16 mb-4 md:mb-8 lg:mb-12">
            <img
              alt="logo"
              src="/TriVista_Horizontal_Logo_White.png"
              className="w-[300px]"
            />
          </div>
          <CognitoMFASetup 
            onComplete={async () => {
              setMfaSetupRequired(false);
              setUserEmail("");
              setTotpSetupDetails(null);
              
              try {
                const isAdmin = await checkAdminAuthStatus();
                if (isAdmin) {
                  await applyRememberMe();
                  history.push("/admin/users");
                } else {
                  setError("root", {
                    message: "Access denied. You don't have admin privileges.",
                  });
                  await signOut();
                }
              } catch (error) {
                console.log("Error after MFA setup:", error);
                setError("root", {
                  message: "Error verifying admin privileges after MFA setup.",
                });
                await signOut();
              }
            }}
            onCancel={() => {
              setMfaSetupRequired(false);
              setUserEmail("");
              setTotpSetupDetails(null);
            }}
            totpSetupDetails={totpSetupDetails}
            userEmail={userEmail}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-zinc-900">
      <div
        className="fixed w-full h-full z-0 bg-cover bg-repeat bg-center"
        style={{ backgroundImage: `url(/bg-new.jpg)` }}
      />

      <div className="flex flex-col h-full items-center justify-center relative z-10">
        <div className="mt-4 md:mt-8 lg:mt-16 mb-4 md:mb-8 lg:mb-12">
          <img
            alt="logo"
            src="/TriVista_Horizontal_Logo_White.png"
            className="w-[300px]"
          />
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="max-w-2xl flex flex-col items-center justify-center mx-8">
            <h1 className="text-3xl text-white text-center lg:tracking-[0.75rem] tracking-[0.5rem]">
             TVIQ Admin Portal
            </h1>
            <p className="mt-6 text-white text-lg text-center">
              Administrative access to TriVistaIQ platform
            </p>
            
            <Card className="mt-4 w-full mx-8 md:mx-auto md:w-auto px-4">
              {errors.root && (
                <Alert variant="destructive" className="mt-4 max-w-96">
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{errors.root.message}</AlertDescription>
                </Alert>
              )}
              
              <div className={`${errors.root ? "" : ""} mt-4`}>
                <Label className="text-xl">Email</Label>
                <Input
                  className="h-8 w-80 sm:w-auto sm:min-w-80"
                  {...register("email")}
                />
                <div className="mt-1 flex gap-3 items-start">
                  {errors.email && (
                    <p className="text-red-500">{errors.email.message}</p>
                  )}
                </div>
              </div>
              
              <div className="mt-4">
                <Label className="text-xl">Password</Label>
                <Input
                  className="h-8 w-80 sm:w-auto sm:min-w-80"
                  type="password"
                  {...register("password")}
                />
                <div className="mt-1 flex gap-3 items-right">
                  {errors.password && (
                    <p className="text-red-500">{errors.password.message}</p>
                  )}
                </div>
              </div>
              <div className="mt-3 flex items-center space-x-2">
                <input
                  id="admin-remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4"
                />
                <Label htmlFor="admin-remember-me" className="text-sm">
                  Remember Me
                </Label>
              </div>
              {/* Forgot Password Link */}
              <div className="text-right">
                <Link
                  to={"/admin/forgot-password"}
                  className="text-sm text-blue-500 hover:underline"
                >
                  Forgot Password?
                </Link>
              </div>
              
              <Button
                className="my-4 w-full px-12 py-6 text-xl rounded-xl"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting && (
                  <ReloadIcon className="w-6 h-6 animate-spin mr-2" />
                )}
                {isSubmitting ? "Signing In..." : "Admin Login"}
              </Button>
            </Card>
          </div>
        </form>
        
        
      </div>
    </div>
  );
};

export default AdminLogin;